/**
 * HiuConsentsListPage — /org/health-info/consents (HIU_FLOW_104)
 *
 * List of all consent requests for the org (newest first): patient name, full ABHA address, masked ABHA
 * number, record types (2, then "+n"), purpose, request date + time, record period, access-until (every
 * status but "Not sent", FT-R7), status + when it changed. Status tabs with counts and a
 * name/ABHA-address search are client-side over the one listConsents result. Row click navigates to the detail page. Follows the card-list
 * convention used across the app (no shared <Table> component exists) — see
 * ManageAppointmentsPage.jsx for the precedent this mirrors.
 *
 * Live status (HIU_FLOW_202): while any row is REQUESTED the list re-GETs itself every
 * POLL_INTERVAL_MS, silently (no spinner, rows kept on a failed tick). One request in flight at a
 * time, skipped while the tab is hidden, stopped after LIST_POLL_WINDOW_MS, on unmount, or once
 * no REQUESTED row is left.
 */
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import AdminPageHeader from '../components/common/AdminPageHeader';
import ConsentStatusChip from '../components/hiu/ConsentStatusChip';
import { POLL_INTERVAL_MS, LIST_POLL_WINDOW_MS } from '../components/hiu/OrgFetchSection';
import { useTheme } from '../theme/ThemeContext';
import { listConsents } from '../services/api/hiuApi';
import { statusTimeLine } from '../components/hiu/consentStatusTime';
import { CONSENT_TABS, countByDisplayStatus, matchesSearch, typesSummary } from '../components/hiu/consentListSelectors';
import { formatDateIST, formatDateTimeISTWithYear, utcIsoToISTInputValue } from '../utils/formatIST';
import { ROUTES } from '../constants/routes';
import { showsAccessUntil, notSentReason } from '../components/hiu/consentOrgs';
import { OTHER_ORGS, OTHER_ORGS_TITLE, ABDM_ICONS } from '../constants/abdmCopy';
import { purposeLabel } from '../constants/abdmPurposes';
import AbdmEnabledGate from '../components/common/AbdmEnabledGate';

const PAGE_SIZE = 10;

// IST calendar-day comparison (R126) — createdAt is a UTC timestamp, dateFrom/dateTo are
// plain YYYY-MM-DD from <input type="date">. Comparing by IST day means a "to" date includes
// every request made that whole IST day.
function dayOf(iso) {
  return iso ? utcIsoToISTInputValue(iso).slice(0, 10) : '';
}

function HiuConsentsListPageBody() {
  const { isDark } = useTheme();
  const navigate = useNavigate();

  const [consents, setConsents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState('ALL');
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');

  // Debounce the search 200 ms; a changed query always returns to page 1.
  useEffect(() => {
    const t = setTimeout(() => { setQuery(searchInput); setPage(1); }, 200);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const items = await listConsents();
      setConsents(items);
    } catch (err) {
      setError(err.userMessage || 'Failed to load consent requests.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Silent refresh for the poll below: swaps the rows in place; a failed tick keeps what is shown.
  const refreshQuietly = useCallback(async () => {
    try {
      setConsents(await listConsents());
      setError('');
    } catch (err) {
      // keep the current rows; the next tick retries
    }
  }, []);

  // Primitive (the effect dep): true while any request is still waiting for the patient.
  const hasRequested = consents.some((c) => c.displayStatus === 'REQUESTED');
  const pollInFlight = useRef(false);
  useEffect(() => {
    if (!hasRequested) return undefined;
    const start = Date.now();
    const id = setInterval(async () => {
      if (Date.now() - start > LIST_POLL_WINDOW_MS) { clearInterval(id); return; }
      if (document.hidden || pollInFlight.current) return;
      pollInFlight.current = true;
      try { await refreshQuietly(); } finally { pollInFlight.current = false; }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [hasRequested, refreshQuietly]);

  const counts = useMemo(() => countByDisplayStatus(consents), [consents]);
  const filtered = useMemo(() => consents
    .filter((c) => {
      const day = dayOf(c.createdAt);
      if (dateFrom && day < dateFrom) return false;
      if (dateTo && day > dateTo) return false;
      if (tab !== 'ALL' && c.displayStatus !== tab) return false;
      return matchesSearch(c, query);
    })
    .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || '')),
  [consents, dateFrom, dateTo, tab, query]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const hasFilter = Boolean(dateFrom || dateTo);
  const tabLabel = CONSENT_TABS.find((t) => t.key === tab).label.toLowerCase();

  const clearFilter = () => { setDateFrom(''); setDateTo(''); setPage(1); };
  const selectTab = (key) => { setTab(key); setPage(1); };

  const textMuted = isDark ? '#9ca3af' : '#6b7280';
  const bg = isDark
    ? 'linear-gradient(135deg, #0E0E0F 0%, #1a1a2e 50%, #0E0E0F 100%)'
    : 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #f8fafc 100%)';

  return (
    <div style={{ minHeight: '100vh', background: bg }}>
      <AdminPageHeader />

      <main style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: isDark ? '#f5f5f7' : '#111827', margin: 0 }}>
              {ABDM_ICONS.OTHER_ORGS} {OTHER_ORGS_TITLE}
            </h1>
            <p style={{ fontSize: 14, color: textMuted, marginTop: 4 }}>
              {OTHER_ORGS.listSubtitle}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={load}
              disabled={loading}
              title="Refresh"
              style={{
                background: isDark ? '#1e293b' : '#f1f5f9',
                color: isDark ? '#f1f5f9' : '#374151',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#d1d5db'}`,
                borderRadius: 8, padding: '10px 14px', fontSize: 14, fontWeight: 600,
                cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.6 : 1,
              }}
            >
              {loading ? '…' : '↻'} Refresh
            </button>
            <button
              onClick={() => navigate(ROUTES.HIU_CONSENT_REQUEST)}
              style={{
                background: '#0369a1', color: '#ffffff', border: 'none',
                borderRadius: 8, padding: '10px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
              }}
            >
              {OTHER_ORGS.newRequest}
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {CONSENT_TABS.map((t) => {
            const active = t.key === tab;
            return (
              <button
                key={t.key}
                onClick={() => selectTab(t.key)}
                aria-pressed={active}
                style={{
                  background: active ? '#0369a1' : (isDark ? '#1e293b' : '#ffffff'),
                  color: active ? '#ffffff' : (isDark ? '#f1f5f9' : '#1f2937'),
                  border: `1px solid ${active ? '#0369a1' : (isDark ? 'rgba(255,255,255,0.25)' : '#9ca3af')}`,
                  borderRadius: 99, padding: '6px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}
              >
                {t.label}{!loading && !error ? ` (${counts[t.key]})` : ""}
              </button>
            );
          })}
        </div>

        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search by patient name or ABHA address"
          aria-label="Search requests"
          style={{
            width: '100%', boxSizing: 'border-box', marginBottom: 12,
            background: isDark ? '#1e293b' : '#ffffff',
            color: isDark ? '#f1f5f9' : '#111827',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.25)' : '#9ca3af'}`,
            borderRadius: 8, padding: '8px 12px', fontSize: 14,
          }}
        />

        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <label style={{ fontSize: 13, color: textMuted, display: 'flex', alignItems: 'center', gap: 6 }}>
            From
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              style={{
                background: isDark ? '#1e293b' : '#ffffff',
                color: isDark ? '#f1f5f9' : '#111827',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#d1d5db'}`,
                borderRadius: 6, padding: '6px 8px', fontSize: 13,
              }}
            />
          </label>
          <label style={{ fontSize: 13, color: textMuted, display: 'flex', alignItems: 'center', gap: 6 }}>
            To
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              style={{
                background: isDark ? '#1e293b' : '#ffffff',
                color: isDark ? '#f1f5f9' : '#111827',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#d1d5db'}`,
                borderRadius: 6, padding: '6px 8px', fontSize: 13,
              }}
            />
          </label>
          {hasFilter && (
            <button
              onClick={clearFilter}
              style={{
                background: isDark ? '#1e293b' : '#f1f5f9',
                color: isDark ? '#f1f5f9' : '#374151',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#d1d5db'}`,
                borderRadius: 6, padding: '6px 12px', fontSize: 13, cursor: 'pointer',
              }}
            >
              Clear filter
            </button>
          )}
        </div>

        {loading && (
          <div style={{ color: textMuted, padding: '2rem 0', textAlign: 'center' }}>Loading consent requests…</div>
        )}

        {!loading && error && (
          <div style={{
            textAlign: 'center', padding: '2rem 1rem',
            color: isDark ? '#f87171' : '#dc2626',
          }}>
            <p>{error}</p>
            <button
              onClick={load}
              style={{
                marginTop: 8, background: isDark ? '#1e293b' : '#f1f5f9',
                color: isDark ? '#f1f5f9' : '#374151',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#d1d5db'}`,
                borderRadius: 6, padding: '6px 14px', fontSize: 13, cursor: 'pointer',
              }}
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !error && consents.length === 0 && (
          <div style={{ textAlign: 'center', padding: '3rem 1rem', color: textMuted }}>
            <div style={{ fontSize: '2rem' }}>{ABDM_ICONS.OTHER_ORGS}</div>
            <p>{OTHER_ORGS.emptyList}</p>
            <p style={{ fontSize: '0.75rem' }}>Start one from the button above.</p>
          </div>
        )}

        {!loading && !error && consents.length > 0 && filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '3rem 1rem', color: textMuted }}>
            <div style={{ fontSize: '2rem' }}>🗓️</div>
            <p>{tab === 'ALL' ? 'No requests match.' : `No ${tabLabel} requests.`}</p>
            <button
              onClick={() => { clearFilter(); setTab('ALL'); setSearchInput(''); setQuery(''); }}
              style={{
                marginTop: 8, background: isDark ? '#1e293b' : '#f1f5f9',
                color: isDark ? '#f1f5f9' : '#374151',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#d1d5db'}`,
                borderRadius: 6, padding: '6px 14px', fontSize: 13, cursor: 'pointer',
              }}
            >
              Clear filters
            </button>
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {pageItems.map((c) => {
              const line = statusTimeLine(c);
              // Granted scope's range when present (backend already prefers it), else the requested one.
              const from = formatDateIST(c.dateFrom || c.requestedDateFrom);
              const to = formatDateIST(c.dateTo || c.requestedDateTo);
              const records = from || to ? `${from || '—'} – ${to || '—'}` : '';
              const status = c.displayStatus;
              const isGranted = status === 'GRANTED';
              return (
              <button
                key={c.consentRequestId}
                onClick={() => navigate(
                  ROUTES.HIU_CONSENT_DETAIL.replace(':consentId', c.consentRequestId)
                )}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  textAlign: 'left', width: '100%', cursor: 'pointer',
                  background: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}`,
                  borderRadius: 10, padding: '14px 16px',
                  ...(isGranted ? { borderLeft: `4px solid ${isDark ? '#16a34a' : '#4ade80'}` } : {}),
                }}
              >
                <div style={{ minWidth: 0, marginRight: 12 }}>
                  <p style={{ margin: 0, fontWeight: 600, color: isDark ? '#f1f5f9' : '#111827' }}>
                    {c.patientName || 'Patient'}
                  </p>
                  <p style={{ margin: '2px 0 0', fontSize: 12, color: textMuted, wordBreak: 'break-all' }}>
                    {c.patientAbhaAddress || '—'}
                  </p>
                  <p style={{ margin: '2px 0 0', fontSize: 12, color: textMuted }}>
                    {`${OTHER_ORGS.abhaNumber}: ${c.patientAbhaNumberMasked || '—'}`}
                  </p>
                  <p style={{ margin: '2px 0 0', fontSize: 12, color: textMuted }}>
                    {`${OTHER_ORGS.scopeTypes}: ${typesSummary(c.hiTypes) || '—'} · ${OTHER_ORGS.purpose}: ${purposeLabel(c.purposeCode) || '—'}`}
                  </p>
                  <p style={{ margin: '2px 0 0', fontSize: 12, color: textMuted }}>
                    {OTHER_ORGS.rowRequested} {formatDateTimeISTWithYear(c.createdAt) || '—'}
                    {records && ` · ${OTHER_ORGS.rowRecords} ${records}`}
                    {showsAccessUntil(status) && ` · ${OTHER_ORGS.rowAccessUntil} ${formatDateTimeISTWithYear(c.expiresAt) || '—'}`}
                    {status === 'REQUESTED' && c.requestExpiryAt && ` · ${OTHER_ORGS.requestExpires} ${formatDateTimeISTWithYear(c.requestExpiryAt)}`}
                    {line && ` · ${line.label} ${line.at}`}
                  </p>
                  {isGranted && (
                    <p style={{
                      margin: '2px 0 0', fontSize: 12, fontWeight: c.recordsReceived > 0 ? 600 : 400,
                      color: c.recordsReceived > 0 ? (isDark ? '#bbf7d0' : '#166534') : textMuted,
                    }}>
                      {c.recordsReceived > 0 ? OTHER_ORGS.recordsReceivedRow(c.recordsReceived) : OTHER_ORGS.noRecordsYet}
                    </p>
                  )}
                  {status === 'SUBMIT_FAILED' && notSentReason(c, 'list') && (
                    <p style={{ margin: '2px 0 0', fontSize: 12, color: isDark ? '#fde68a' : '#92400e' }}>
                      {notSentReason(c, 'list')}
                    </p>
                  )}
                  {status === 'SUBMIT_FAILED' && !notSentReason(c, 'list') && (
                    <p
                      title={c.failureReason || undefined}
                      style={{
                        margin: '2px 0 0', fontSize: 12, color: isDark ? '#fde68a' : '#92400e',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 520,
                      }}
                    >
                      {c.failureReason || 'The request could not be sent to ABDM.'}
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                  <ConsentStatusChip displayStatus={status} isDark={isDark} />
                  {isGranted ? (
                    <span style={{
                      background: '#0369a1', color: '#ffffff', borderRadius: 8, padding: '8px 12px',
                      fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap',
                    }}>
                      {OTHER_ORGS.viewRecords}
                    </span>
                  ) : (
                    <span aria-hidden="true" style={{ color: textMuted, fontSize: 22, lineHeight: 1 }}>›</span>
                  )}
                </div>
              </button>
              );
            })}
          </div>
        )}

        {!loading && !error && filtered.length > 0 && totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 20 }}>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage === 1}
              style={{
                background: isDark ? '#1e293b' : '#f1f5f9',
                color: isDark ? '#f1f5f9' : '#374151',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#d1d5db'}`,
                borderRadius: 6, padding: '6px 14px', fontSize: 13,
                cursor: safePage === 1 ? 'default' : 'pointer', opacity: safePage === 1 ? 0.5 : 1,
              }}
            >
              ← Previous
            </button>
            <span style={{ fontSize: 13, color: textMuted }}>
              Page {safePage} of {totalPages} ({filtered.length} request{filtered.length === 1 ? '' : 's'})
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage === totalPages}
              style={{
                background: isDark ? '#1e293b' : '#f1f5f9',
                color: isDark ? '#f1f5f9' : '#374151',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#d1d5db'}`,
                borderRadius: 6, padding: '6px 14px', fontSize: 13,
                cursor: safePage === totalPages ? 'default' : 'pointer', opacity: safePage === totalPages ? 0.5 : 1,
              }}
            >
              Next →
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

export default function HiuConsentsListPage() {
  return (
    <AbdmEnabledGate>
      <HiuConsentsListPageBody />
    </AbdmEnabledGate>
  );
}
